import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import * as JsSIP from "jssip";
import Swal from 'sweetalert2';
import { useNoiseSuppression } from '../hooks/useNoiseSuppression';

const SIPContext = createContext();

export const SIPProvider = ({ children }) => {
    const { getProcessedStream, setNoiseCancellation, cleanup: cleanupNoise } = useNoiseSuppression();
    const cleanupNoiseRef = useRef(cleanupNoise);
    useEffect(() => { cleanupNoiseRef.current = cleanupNoise; }, [cleanupNoise]);

    const [isNoiseCancellationEnabled, setIsNoiseCancellationEnabled] = useState(() =>
        localStorage.getItem('noiseCancellationEnabled') !== 'false'
    );
    const isNoiseCancellationEnabledRef = useRef(isNoiseCancellationEnabled);
    useEffect(() => {
        isNoiseCancellationEnabledRef.current = isNoiseCancellationEnabled;
        localStorage.setItem('noiseCancellationEnabled', isNoiseCancellationEnabled);
    }, [isNoiseCancellationEnabled]);

    const [session, setSession] = useState(null);
    const [config, setConfig] = useState(() => {
        const saved = localStorage.getItem('sipConfig');
        return saved ? JSON.parse(saved) : {
            username: process.env.REACT_APP_SIP_USERNAME || '',
            password: process.env.REACT_APP_SIP_PASSWORD || '',
            domain: process.env.REACT_APP_SIP_DOMAIN || '',
            webrtcUrl: process.env.REACT_APP_WEBRTC_URL || ''
        };
    });

    const [audioDevices, setAudioDevices] = useState({
        microphones: [],
        speakers: []
    });
    const [selectedDevices, setSelectedDevices] = useState({
        microphone: 'default',
        speaker: 'default'
    });

    const getAudioDevices = useCallback(async () => {
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });

            const devices = await navigator.mediaDevices.enumerateDevices();
            const microphones = devices.filter(device => device.kind === 'audioinput');
            const speakers = devices.filter(device => device.kind === 'audiooutput');

            setAudioDevices({ microphones, speakers });

            if (!selectedDevices.speaker && speakers.length > 0) {
                const builtInSpeaker = speakers.find(s =>
                    s.label.toLowerCase().includes('built-in') ||
                    s.label.toLowerCase().includes('internal') ||
                    (!s.label.toLowerCase().includes('headphone') &&
                        !s.label.toLowerCase().includes('bluetooth'))
                );

                const preferredSpeaker = builtInSpeaker || speakers[0];

                setSelectedDevices(prev => ({
                    ...prev,
                    speaker: preferredSpeaker.deviceId
                }));

                if (remoteAudioRef.current && typeof remoteAudioRef.current.setSinkId === 'function') {
                    remoteAudioRef.current.setSinkId(preferredSpeaker.deviceId).catch(console.error);
                }
            }

            console.log('Found microphones:', microphones);
            console.log('Found speakers:', speakers);
        } catch (error) {
            console.error('Error getting audio devices:', error);
        }
    }, [selectedDevices]);


    const changeMicrophone = useCallback(async (deviceId) => {
        console.log('Changing microphone to:', deviceId);
        setSelectedDevices(prev => ({ ...prev, microphone: deviceId }));

        if (session && session.connection) {
            try {
                const micId = deviceId === 'default' ? '' : deviceId;
                const newStream = await getProcessedStream(micId, isNoiseCancellationEnabledRef.current);
                const audioTrack = newStream.getAudioTracks()[0];
                const sender = session.connection.getSenders().find(s => s.track?.kind === 'audio');

                if (sender) {
                    await sender.replaceTrack(audioTrack);
                    console.log('Microphone changed successfully');
                }
            } catch (error) {
                console.error('Error changing microphone:', error);
            }
        }
    }, [session, getProcessedStream]);

    const changeSpeaker = useCallback(async (deviceId) => {
        console.log('Changing speaker to:', deviceId);
        setSelectedDevices(prev => ({ ...prev, speaker: deviceId }));

        if (remoteAudioRef.current && typeof remoteAudioRef.current.setSinkId === 'function') {
            try {
                await remoteAudioRef.current.setSinkId(deviceId);
                console.log('Speaker changed successfully');
            } catch (error) {
                console.error('Error changing speaker:', error);
            }
        }
    }, []);

    useEffect(() => {
        getAudioDevices();
        navigator.mediaDevices.addEventListener('devicechange', getAudioDevices);
        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', getAudioDevices);
        };
    }, [getAudioDevices]);

    React.useEffect(() => {
        localStorage.setItem('sipConfig', JSON.stringify(config));
    }, [config]);

    const [status, setStatus] = useState('disconnected');
    const [callStatus, setCallStatus] = useState('idle');
    const [caller, setCaller] = useState('');
    const [isHeld, setIsHeld] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    // Single source of truth for the header dialpad icon — lets the one
    // app-wide call popup double as the outbound dialer instead of opening a second popup.
    const [isDialerOpen, setIsDialerOpen] = useState(false);
    const openDialer = useCallback(() => setIsDialerOpen(true), []);
    const closeDialer = useCallback(() => setIsDialerOpen(false), []);

    const uaRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const ringtoneCtxRef = useRef(null);
    const ringtoneIntervalRef = useRef(null);
    const sessionRef = useRef(null);
    const timerRef = useRef(null);

    const updateConfig = useCallback((newConfig) => {
        setConfig(newConfig);
    }, []);

    const playRingtone = useCallback(() => {
        if (ringtoneCtxRef.current) {
            ringtoneCtxRef.current.close();
            ringtoneCtxRef.current = null;
        }
        clearInterval(ringtoneIntervalRef.current);

        const australianRing = (ctx, startTime) => {
            const ringPulse = (start) => {
                const osc1 = ctx.createOscillator();
                const osc2 = ctx.createOscillator();
                const gain = ctx.createGain();

                osc1.connect(gain);
                osc2.connect(gain);
                gain.connect(ctx.destination);

                osc1.type = 'sine';
                osc2.type = 'sine';
                osc1.frequency.value = 413;
                osc2.frequency.value = 438;

                gain.gain.setValueAtTime(0.3, start);
                gain.gain.setValueAtTime(0.3, start + 0.4);
                gain.gain.linearRampToValueAtTime(0, start + 0.45);

                osc1.start(start);
                osc2.start(start);
                osc1.stop(start + 0.45);
                osc2.stop(start + 0.45);
            };

            ringPulse(startTime);
            ringPulse(startTime + 0.6);
        };

        const ring = () => {
            if (ringtoneCtxRef.current) {
                ringtoneCtxRef.current.close().catch(() => {});
            }
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            ringtoneCtxRef.current = ctx;
            australianRing(ctx, ctx.currentTime);
        };

        ring();
        ringtoneIntervalRef.current = setInterval(ring, 3000);
    }, []);

    const stopRingtone = useCallback(() => {
        clearInterval(ringtoneIntervalRef.current);
        ringtoneIntervalRef.current = null;
        if (ringtoneCtxRef.current) {
            ringtoneCtxRef.current.close();
            ringtoneCtxRef.current = null;
        }
    }, []);

    const playCallEndSound = useCallback(() => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const now = ctx.currentTime;

        // Dual-tone beep - mirrors the classic telephone "call disconnected" signal
        const dualBeep = (start, duration) => {
            [950, 1400].forEach((freq) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.type = 'sine';
                osc.frequency.value = freq;

                gain.gain.setValueAtTime(0.0001, start);
                gain.gain.exponentialRampToValueAtTime(0.12, start + 0.015);
                gain.gain.setValueAtTime(0.12, start + duration - 0.03);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

                osc.start(start);
                osc.stop(start + duration);
            });
        };

        // Two short beeps, like a call disconnecting
        dualBeep(now, 0.18);
        dualBeep(now + 0.28, 0.18);

        setTimeout(() => ctx.close().catch(() => {}), 800);
    }, []);

    useEffect(() => {
        if (callStatus === 'incoming') {
            playRingtone();
        } else {
            stopRingtone();
        }
        return () => stopRingtone();
    }, [callStatus, playRingtone, stopRingtone]);

    const connect = useCallback(() => {
        console.log(config.webrtcUrl, "--------", config.username, "-------", config.password, "-------", config.domain)
        if (!config.webrtcUrl || !config.username || !config.password || !config.domain) {
            console.warn('SIP config incomplete, cannot connect');
            return;
        }

        if (uaRef.current) {
            uaRef.current.stop();
        }

        try {
            const socket = new JsSIP.WebSocketInterface(config.webrtcUrl);
            const configuration = {
                sockets: [socket],
                uri: `sip:${config.username}@${config.domain}`,
                password: config.password,
                display_name: config.username,
                pcConfig: {
                    iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }],
                    iceTransportPolicy: 'all',
                    rtcpMuxPolicy: 'require',
                    bundlePolicy: 'max-bundle',
                },
                rtcOfferConstraints: {
                    offerToReceiveAudio: 1,
                    offerToReceiveVideo: 0,
                },
                sessionTimers: false,
                register: true,
                hackIpInContact: true,
                hackViaTcp: true,
                connection_recovery_min_interval: 2,
                connection_recovery_max_interval: 30
            };

            const newUa = new JsSIP.UA(configuration);
            uaRef.current = newUa;

            // Ignore events from a UA instance that connect() has since replaced/stopped -
            // JsSIP keeps emitting teardown events (disconnected/unregistered) asynchronously
            // after .stop(), and without this guard they overwrite status set by the new UA,
            // which re-triggers Header.js's auto-reconnect effect and causes an infinite
            // register/unregister loop against the SIP server.
            const isCurrent = () => uaRef.current === newUa;

            newUa.on('connecting', () => { if (!isCurrent()) return; console.log('UA connecting'); setStatus('connecting'); });
            newUa.on('connected', () => { if (!isCurrent()) return; console.log('UA connected'); setStatus('connected'); });
            newUa.on('disconnected', () => { if (!isCurrent()) return; console.log('UA disconnected'); setStatus('disconnected'); });
            newUa.on('registered', () => { if (!isCurrent()) return; console.log('UA registered'); setStatus('connected'); });
            newUa.on('unregistered', () => {
                if (!isCurrent()) return;
                console.log('UA unregistered');
                setStatus(newUa.isConnected() ? 'connecting' : 'disconnected');
            });
            newUa.on('registrationFailed', (e) => {
                if (!isCurrent()) return;
                console.warn('Registration failed:', e.cause);
                setStatus(newUa.isConnected() ? 'connected' : 'disconnected');

                Swal.fire({
                    title: "Registration Failed",
                    text: "",
                    confirmButtonColor: "#3085d6",
                    icon: 'warning'
                });
                return;
            });

            newUa.on('newRTCSession', (data) => {
                if (!isCurrent()) return;
                const newSession = data.session;

                console.log('📞 New RTC Session:', {
                    direction: newSession.direction,
                    currentSession: !!sessionRef.current,
                    from: newSession.remote_identity.uri.user
                });

                // ✅ INCOMING CALL HANDLER - Block if agent is busy
                if (newSession.direction === 'incoming') {
                    // Check ONLY sessionRef.current (NOT callStatus - it's stale in closures!)
                    if (sessionRef.current) {
                        console.log('❌ BLOCKING incoming call - Agent already on a call');
                        newSession.terminate({
                            status_code: 486, // SIP: Busy Here
                            reason_phrase: 'Busy Here'
                        });
                        return; // EXIT - Don't process this call any further
                    }

                    // Only reaches here if sessionRef.current is null (agent is free)
                    console.log('✅ ACCEPTING incoming call from:', newSession.remote_identity.uri.user);
                    setCallStatus('incoming');
                    setCaller(newSession.remote_identity.display_name || newSession.remote_identity.uri.user);
                } else {
                    // ✅ OUTGOING CALL - set caller to the number being dialed
                    setCaller(newSession.remote_identity.display_name || newSession.remote_identity.uri.user);
                }

                // Now set this as the active session
                sessionRef.current = newSession;
                setSession(newSession);

                let attachDebounceTimer = null;
                const attachRemoteStream = () => {
                    console.log("attachRemoteStream Entered")
                    clearTimeout(attachDebounceTimer);
                    attachDebounceTimer = setTimeout(() => {
                        const liveSession = sessionRef.current;

                        if (!liveSession?.connection || !remoteAudioRef.current) {
                            console.warn('EXIT - connection:', liveSession?.connection, '| audioRef:', remoteAudioRef.current);
                            return;
                        }

                        console.log("liveSession   ", liveSession)
                        const streams = liveSession.connection.getRemoteStreams();
                        console.log("streams   ", streams)
                        const remoteStream = streams[0];
                        console.log("remoteStream  ", remoteStream)
                        if (!remoteStream) return;

                        const audioEl = remoteAudioRef.current;
                        console.log("audioEl   :   ", audioEl)
                        if (audioEl.srcObject === remoteStream) {
                            audioEl.play().catch(console.error);
                            return;
                        }

                        console.log('Attaching remote stream, tracks:', remoteStream.getTracks().length);
                        audioEl.srcObject = remoteStream;
                        audioEl.play().catch(err => console.error('Play error:', err));
                    }, 100);
                };

                newSession.on('peerconnection', (data) => {
                    const pc = data.peerconnection;
                    pc.addEventListener('track', (e) => {
                        if (e.track.kind === 'audio') attachRemoteStream();
                    });
                    pc.addEventListener('addstream', () => attachRemoteStream());
                });

                // Early media - fires when remote side sends 183 Session Progress
                newSession.on('progress', (e) => {
                    console.log('Call progress - early media possible');
                    if (e.originator === 'remote') {
                        attachRemoteStream();
                    }
                });

                newSession.on('hold', () => setIsHeld(true));
                newSession.on('unhold', () => { setIsHeld(false); setTimeout(attachRemoteStream, 500); });
                newSession.on('connecting', () => setCallStatus('calling'));
                newSession.on('accepted', () => { setCallStatus('active'); setTimeout(attachRemoteStream, 100); });

                const resetCall = () => {
                    console.log('🔄 Resetting call state');
                    cleanupNoiseRef.current?.();
                    sessionRef.current = null;
                    setCallStatus('idle');
                    setSession(null);
                    setIsHeld(false);
                    setIsMuted(false);
                    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
                    playCallEndSound();
                };

                newSession.on('sdp', (e) => {
                    console.log(`📄 ${e.originator === 'local' ? 'Local' : 'Remote'} SDP ${e.type}:\n${e.sdp}`);
                });

                newSession.on('ended', resetCall);
                newSession.on('failed', (e) => {
                    console.log('Call failed:', e.cause);
                    if (e.message) {
                        console.log('SIP failure response:', {
                            status_code: e.message.status_code,
                            reason_phrase: e.message.reason_phrase,
                            warning: e.message.getHeader?.('Warning'),
                            reason_header: e.message.getHeader?.('Reason'),
                        });
                    }
                    resetCall();
                });
            });

            newUa.start();
        } catch (error) {
            console.error('Failed to initialize JsSIP:', error);
            setStatus('disconnected');
        }
    }, [config]);

    const makeCall = useCallback(async (number) => {
        if (!uaRef.current || !uaRef.current.isConnected()) {
            console.error('UA not connected');
            return;
        }
        const micId = selectedDevices.microphone === 'default' ? '' : selectedDevices.microphone;
        const stream = await getProcessedStream(micId, isNoiseCancellationEnabledRef.current);
        uaRef.current.call(`sip:${number}@${config.domain}`, {
            mediaStream: stream,
        });
    }, [config.domain, getProcessedStream, selectedDevices.microphone]);

    const hangup = useCallback(() => { if (session) session.terminate(); }, [session]);

    const answer = useCallback(async () => {
        if (session && callStatus === 'incoming') {
            const micId = selectedDevices.microphone === 'default' ? '' : selectedDevices.microphone;
            const stream = await getProcessedStream(micId, isNoiseCancellationEnabledRef.current);
            session.answer({ mediaStream: stream });
        }
    }, [session, callStatus, getProcessedStream, selectedDevices.microphone]);

    const reject = useCallback(() => {
        if (session && callStatus === 'incoming') session.terminate();
    }, [session, callStatus]);

    const hold = useCallback(() => {
        if (session && !session.isOnHold().local) {
            try { session.hold(); } catch (e) { console.error('Hold failed:', e); }
        }
    }, [session]);

    const unhold = useCallback(() => {
        if (session && session.isOnHold().local) {
            try { session.unhold(); } catch (e) { console.error('Unhold failed:', e); }
        }
    }, [session]);

    const mute = useCallback(() => { if (session) { session.mute(); setIsMuted(true); } }, [session]);
    const unmute = useCallback(() => { if (session) { session.unmute(); setIsMuted(false); } }, [session]);

    const sendDTMF = useCallback((tone) => {
        if (session && session.isEstablished()) {
            try { session.sendDTMF(tone); } catch (e) { console.error('DTMF failed:', e); }
        }
    }, [session]);

    const toggleNoiseCancellation = useCallback(() => {
        const newValue = !isNoiseCancellationEnabledRef.current;
        setIsNoiseCancellationEnabled(newValue);
        setNoiseCancellation(newValue);
    }, [setNoiseCancellation]);

    useEffect(() => {
        if (callStatus === 'active') {
            setCallDuration(0);
            timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [callStatus]);


    const disconnect = useCallback(() => {
        if (uaRef.current) { uaRef.current.stop(); setStatus('disconnected'); }
    }, []);

    const unregister = useCallback(() => {
        if (uaRef.current && uaRef.current.isRegistered()) uaRef.current.unregister();
    }, []);

    const value = {
        status, callStatus, caller, isHeld, isMuted, remoteAudioRef,
        config, updateConfig, connect, disconnect, unregister,
        makeCall, hangup, answer, reject, hold, unhold, mute, unmute,
        sendDTMF, callDuration,
        isDialerOpen, openDialer, closeDialer,
        audioDevices,
        selectedDevices,
        changeMicrophone,
        changeSpeaker,
        isNoiseCancellationEnabled,
        toggleNoiseCancellation,
    };

    // Ensure audio element is mounted first
    const [audioReady, setAudioReady] = useState(false);

    return (
        <SIPContext.Provider value={value}>
            <audio
                ref={(el) => {
                    remoteAudioRef.current = el;
                    if (el && !audioReady) setAudioReady(true);
                }}
                autoPlay
            />
            {audioReady && children}
        </SIPContext.Provider>
    );
};

export const useSIP = () => {
    const context = useContext(SIPContext);
    if (!context) throw new Error('useSIP must be used within SIPProvider');
    return context;
};